import Cookies from 'universal-cookie';
import { hasValidTokenRaw, readAuthCookieFromDocument } from 'utils/authCookieRead';
import { getCookieDomainForName, getCookieSecureDefault } from 'utils/cookieDomain';

const LEGACY_DOMAIN = '.ommnio.com';
const LEGACY_KEYS = ['company', 'token', 'impersonate', 'locale'];
// Keep in sync with utils/localeCookie APP_LOCALES (avoid importing it — Cookie cycle).
const APP_LOCALES = ['en', 'ca', 'eu', 'pt', 'es'];

const expireCookie = (name, domain) => {
  const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  document.cookie = domain ? `${base}; domain=${domain}` : base;
};

const rewriteAuthCookie = (cookies, name, value) => {
  if (!value) {
    return;
  }
  const domain = getCookieDomainForName(name);
  cookies.remove(name, { path: '/' });
  cookies.set(name, value, {
    path: '/',
    secure: getCookieSecureDefault(),
    sameSite: 'Lax',
    ...(domain ? { domain } : {}),
  });
};

const rewriteHostOnlyCookie = (cookies, name, value) => {
  if (value == null || value === '') {
    return;
  }
  cookies.set(name, value, {
    path: '/',
    secure: getCookieSecureDefault(),
    sameSite: 'Lax',
  });
};

const readSupportedLocale = value =>
  typeof value === 'string' && APP_LOCALES.includes(value) ? value : null;

const hasValidToken = hasValidTokenRaw;

const scrubStaleHostOnlyAuthCookies = cookies => {
  ['token', 'impersonate'].forEach(name => {
    const sharedDomain = getCookieDomainForName(name);
    if (!sharedDomain) {
      return;
    }
    // Auth tokens live on the shared domain; drop host-only shadows (including empty "").
    cookies.remove(name, { path: '/' });
  });
};

/** Expire legacy `.ommnio.com` cookies and migrate auth session to APP_DOMAIN-scoped cookies. */
export default function legacyCookieCleanup() {
  if (typeof document === 'undefined') {
    return;
  }

  const cookies = new Cookies();
  const legacyToken = cookies.get('token', { path: '/', domain: LEGACY_DOMAIN });
  const hostToken = cookies.get('token');
  const tokenBefore = hasValidToken(hostToken)
    ? hostToken
    : hasValidToken(legacyToken)
      ? legacyToken
      : null;
  const impersonateBefore =
    cookies.get('impersonate') || cookies.get('impersonate', { path: '/', domain: LEGACY_DOMAIN });
  // RN signing WebView still sets locale on `.ommnio.com`; keep it as a host-only cookie
  // so preferredLocale() can read it after legacy domain cookies are expired.
  const localeBefore =
    readSupportedLocale(cookies.get('locale')) ||
    readSupportedLocale(cookies.get('locale', { path: '/', domain: LEGACY_DOMAIN }));

  LEGACY_KEYS.forEach(key => {
    expireCookie(key, LEGACY_DOMAIN);
  });

  scrubStaleHostOnlyAuthCookies(cookies);

  if (tokenBefore && !readAuthCookieFromDocument('token')) {
    cookies.remove('token', { path: '/' });
    rewriteAuthCookie(cookies, 'token', tokenBefore);
  }
  if (impersonateBefore && !cookies.get('impersonate')) {
    cookies.remove('impersonate', { path: '/' });
    rewriteAuthCookie(cookies, 'impersonate', impersonateBefore);
  }
  if (localeBefore && !readSupportedLocale(cookies.get('locale'))) {
    rewriteHostOnlyCookie(cookies, 'locale', localeBefore);
  }
}
